import type { Cell } from '@/model/cell';
import { RectRange } from '@/model/range';
import type { Sheet } from '@/model/sheet';
import { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';

export const $highlight = atom<Vector2>(new Vector2(0, 0));
export const $selections = atom<RectRange[]>([]);

export const $tempSelection = atom<RectRange | null>(null);
export const $tempUnion = atom<boolean>(true);

export function addSelection(range: RectRange) {
  const selections = $selections.get();

  if (selections.length === 0) {
    $selections.set([range]);
    return;
  } else if (selections.length === 1) {
    const selection = selections[0]!;

    $selections.set([...selection.union(range)]);
    return;
  }

  $selections.set([...selections, range]);
}

export function removeSelection(range: RectRange) {
  const selections = $selections.get();
  const diff = selections
    .flatMap(selection => [...selection.subtract(range)])
    .filter(s => !s.isOutside());
  $selections.set(diff);
}

export function setSelection(selection: RectRange) {
  $selections.set([selection]);
}

export function clearSelections() {
  $selections.set([]);
}

export function setTempEnd(pos: Vector2) {
  const temp = $tempSelection.get();
  if (!temp) return;
  $tempSelection.set(new RectRange(temp.start, pos));
}

export function addTempSelection() {
  const temp = $tempSelection.get();
  if (!temp) return;
  $tempSelection.set(null);
  if (!$tempUnion.get()) {
    removeSelection(temp);
    return;
  }
  addSelection(temp);
}

export function selectionContains(pos: Vector2): boolean {
  if ($tempSelection.get()?.contains(pos)) return $tempUnion.get();

  const selections = $selections.get();
  for (let i = selections.length - 1; i >= 0; i--) {
    const selection = selections[i]!;

    if (selection.contains(pos)) return true;
  }

  return false;
}

export function getSelectedCells(sheet: Sheet): Cell[] {
  const cells: Cell[] = [];
  const selections = $selections.get();
  for (let i = selections.length - 1; i >= 0; i--) {
    const selection = selections[i]!;
    cells.push(...selection.getCells(sheet));
  }
  return cells;
}

export function* iterateCellPoses(): Iterable<Vector2> {
  const selections = $selections.get();
  for (let i = selections.length - 1; i >= 0; i--) {
    const selection = selections[i]!;
    yield* selection.iterateCellPoses();
  }
}

export function useSelected(pos: Vector2) {
  useStore($selections);
  useStore($tempSelection);
  return selectionContains(pos);
}

export function useSelectedAndAdjacent(
  pos: Vector2,
): [
  self: boolean,
  above: boolean,
  below: boolean,
  left: boolean,
  right: boolean,
] {
  const selections = useStore($selections);
  const temp = useStore($tempSelection);
  const self = useSelected(pos);
  const selectedPos = useStore($highlight);
  if (!self) {
    return [false, false, false, false, false];
  }
  if (
    (!temp ||
      (temp.start.equals(selectedPos) && temp.end.equals(selectedPos))) &&
    (selections.length === 0 ||
      (selections.length === 1 &&
        selections[0]!.start.equals(selectedPos) &&
        selections[0]!.end.equals(selectedPos)))
  ) {
    return [false, false, false, false, false];
  }
  return [
    true,
    selectionContains(pos.add(new Vector2(0, -1))),
    selectionContains(pos.add(new Vector2(0, 1))),
    selectionContains(pos.add(new Vector2(-1, 0))),
    selectionContains(pos.add(new Vector2(1, 0))),
  ];
}

export function useColSelected(col: number) {
  const selections = useStore($selections);
  const temp = useStore($tempSelection);
  const tempUnion = useStore($tempUnion);
  if (temp) {
    if (
      tempUnion &&
      temp.isColRange() &&
      temp.min.y <= 0 &&
      temp.min.x <= col &&
      col <= temp.max.x
    )
      return true;
  }
  return (
    selections.some(
      selection =>
        selection.isColRange() &&
        selection.min.y <= 0 &&
        selection.min.x <= col &&
        col <= selection.max.x,
    ) &&
    (tempUnion || !temp || !temp.inCol(col))
  );
}

export function useSelectionInCol(col: number) {
  const selections = useStore($selections);
  const temp = useStore($tempSelection);
  const tempUnion = useStore($tempUnion);
  if (temp) {
    if (tempUnion && temp.inCol(col)) return true;
  }
  return selections.some(selection => selection.inCol(col));
}

export function useRowSelected(row: number) {
  const selections = useStore($selections);
  const temp = useStore($tempSelection);
  const tempUnion = useStore($tempUnion);
  if (temp) {
    if (
      tempUnion &&
      temp.isRowRange() &&
      temp.min.x <= 0 &&
      temp.min.y <= row &&
      row <= temp.max.y
    )
      return true;
  }
  return (
    selections.some(
      selection =>
        selection.isRowRange() &&
        selection.min.x <= 0 &&
        selection.min.y <= row &&
        row <= selection.max.y,
    ) &&
    (tempUnion || !temp || !temp.inRow(row))
  );
}

export function useSelectionInRow(row: number) {
  const selections = useStore($selections);
  const temp = useStore($tempSelection);
  const tempUnion = useStore($tempUnion);
  if (temp) {
    if (tempUnion && temp.inRow(row)) return true;
  }
  return selections.some(selection => selection.inRow(row));
}

export function useAllSelected() {
  return useStore($selections).some(selection => selection.isAllRange());
}
